import { Link, useSearchParams } from 'react-router-dom';
import { ListBulletIcon, ChartBarIcon } from '@heroicons/react/24/outline';

type ViewMode = 'list' | 'analytics';

interface ViewToggleProps {
    currentView: ViewMode;
}

/**
 * Toggle component for switching between list and analytics views.
 * Uses Links to update the URL while preserving other query params.
 * When switching to analytics, removes the 'archived' param since that filter is hidden.
 */
export function ViewToggle({ currentView }: ViewToggleProps) {
    const [searchParams] = useSearchParams();

    // Build URL for list view (removes 'view' param)
    const getListUrl = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('view');
        params.delete('archived'); // Clean up archived when going back to list
        const queryString = params.toString();
        return queryString ? `?${queryString}` : '';
    };

    // Build URL for analytics view (adds 'view=analytics', removes 'archived')
    const getAnalyticsUrl = () => {
        const params = new URLSearchParams(searchParams);
        params.set('view', 'analytics');
        params.delete('archived'); // Analytics view doesn't use archived filter
        return `?${params.toString()}`;
    };

    const options: { label: string; value: ViewMode; icon: typeof ListBulletIcon; getUrl: () => string }[] = [
        { label: 'List', value: 'list', icon: ListBulletIcon, getUrl: getListUrl },
        { label: 'Analytics', value: 'analytics', icon: ChartBarIcon, getUrl: getAnalyticsUrl },
    ];

    return (
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary p-1">
            {options.map((option) => {
                const isActive = currentView === option.value;
                const Icon = option.icon;
                return (
                    <Link
                        key={option.value}
                        to={option.getUrl()}
                        className={`
                            flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                            ${
                                isActive
                                    ? 'bg-accent dark:bg-primary-light text-white'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark'
                            }
                        `}
                        aria-current={isActive ? 'page' : undefined}
                    >
                        <Icon className="h-4 w-4" />
                        {option.label}
                    </Link>
                );
            })}
        </div>
    );
}
